import type { WidgetTheme, Feature, Locale, DisplayMode } from "../types";
import { FEATURE_LABELS, FEATURE_ICONS } from "../core/feature-selector";
import { generateStyles } from "./styles";
import { t } from "./i18n";
import { assetUrl } from "../core/origin";

export interface ContainerCallbacks {
  onPanelOpen: (feature?: Feature, ch?: string) => void;
  onPanelClose: () => void;
  /** 展开模式下点击 IM 图标 → 直接深链跳转（而非打开面板） */
  onImClick?: (channelId: string, value: string, name: string) => void;
}

function svgPhone(): string {
  return '<svg viewBox="0 0 24 24" width="24" height="24" fill="none"><path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 01.91-.27 11.36 11.36 0 003.68.59 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.45.59 3.57a1 1 0 01-.27.9l-2.2 2.32z" fill="white"/></svg>';
}

function svgChat(): string {
  return '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2v10z"/></svg>';
}

function svgBack(): string {
  return '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>';
}

function svgX(): string {
  return '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
}

function svgCallback(): string {
  return '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16.5a2 2 0 01-2 2h-1.5l-3.5 3.5v-3.5H6a2 2 0 01-2-2V6a2 2 0 012-2h13a2 2 0 012 2v10.5z"/><path d="M14 9l-3 3 3 3"/></svg>';
}

function svgBot(): string {
  return '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="3"/><circle cx="9" cy="11" r="1.5" fill="white" stroke="none"/><circle cx="15" cy="11" r="1.5" fill="white" stroke="none"/><line x1="9" y1="16" x2="15" y2="16"/></svg>';
}

const IM_LOGO: Record<string, string> = {
  whatsapp: '/imlogo/whatsapp.svg', messenger: '/imlogo/messenger.svg',
  instagram: '/imlogo/instagram.svg', telegram: '/imlogo/telegram.svg',
  line: '/imlogo/line.svg', wechat: '/imlogo/wechat.svg',
  zalo: '/imlogo/Zalo.svg', viber: '/imlogo/viber.svg',
};

let _shadowRoot: ShadowRoot | null = null;
let _theme: WidgetTheme | null = null;
let _locale: Locale = "zh-CN";
let _activeFeature: Feature | null = null;
let _features: Feature[] = [];
let _imChannels: Array<{ id: string; name: string; icon: string; value?: string }> = [];
let _callbacks: ContainerCallbacks | null = null;
let _panelOpen = false;
let _displayMode: DisplayMode = "collapsed";
let _unread = 0;

function $(sel: string): HTMLElement | null {
  return _shadowRoot?.querySelector(sel) as HTMLElement | null;
}

export interface ExpandedOptions {
  showLabels?: boolean;
  direction?: "vertical" | "horizontal";
  order?: string[];
}

/** 展开模式：把「语音/文本/回拨 + 各 IM」渲染成独立图标启动器，并隐藏 FAB。 */
export function setFloatingIcons(features: Feature[], imChannels: Array<{ id: string; name: string; icon: string; value?: string }>, opts: ExpandedOptions = {}): void {
  _features = features;
  _imChannels = imChannels;
  const iconsContainer = $("#oc-icons");
  const fab = $("#oc-fab");
  if (!iconsContainer) return;
  const c = _theme?.primaryColor || "#533afd";
  const showLabels = opts.showLabels !== false;
  // label 始终渲染：常显模式下内联展示；非常显（oc-no-labels）下由 CSS 改为悬停气泡。
  const item = (label: string, inner: string, attrs: string): string =>
    '<div class="oc-launch-item">' +
      '<span class="oc-launch-label">' + label + '</span>' +
      '<button class="oc-launch-btn" ' + attrs + ' aria-label="' + label + '">' + inner + '</button>' +
    '</div>';
  const items: Array<{ key: string; html: string }> = [];
  if (features.includes("voice")) items.push({ key: "voice", html: item(t("home.voice", _locale), svgPhone(), 'data-feature="voice" style="background:' + c + ';color:#fff"') });
  if (features.includes("text_chat")) items.push({ key: "text_chat", html: item(t("home.text", _locale), svgChat(), 'data-feature="text_chat" style="background:' + c + ';color:#fff"') });
  if (features.includes("callback")) items.push({ key: "callback", html: item(t("home.callback", _locale), svgCallback(), 'data-feature="callback" style="background:' + c + ';color:#fff"') });
  if (features.includes("im") && imChannels.length > 0) {
    const channelColors: Record<string, string> = { whatsapp: "#25D366", messenger: "#0084FF", instagram: "#E4405F", telegram: "#26A5E4", line: "#00B900", wechat: "#07C160", zalo: "#0068FF", viber: "#7360F2" };
    for (const ch of imChannels) {
      const col = channelColors[ch.id] || c;
      const logoSrc = IM_LOGO[ch.id] ? assetUrl(IM_LOGO[ch.id]) : '';
      const inner = logoSrc
        ? '<img src="' + logoSrc + '" alt="' + ch.name + '"/>'
        : '<span style="font-weight:bold;font-size:13px;color:' + col + '">' + ch.name.charAt(0) + '</span>';
      items.push({ key: "im:" + ch.id, html: item(ch.name, inner, 'data-feature="im" data-ch="' + ch.id + '" data-value="' + encodeURIComponent(ch.value || "") + '" data-name="' + ch.name + '" style="background:#fff;border:2px solid ' + col + '"') });
    }
  }
  // 按 expandedOrder 排序：在排序数组中的按其顺序在前，其余保持默认顺序排后
  const order = opts.order || [];
  if (order.length > 0) {
    items.sort((a, b) => {
      const ia = order.indexOf(a.key), ib = order.indexOf(b.key);
      if (ia === -1 && ib === -1) return 0;
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  }
  const html = items.map((i) => i.html).join("");
  const hasIcons = html.length > 0;
  iconsContainer.className = "oc-dir-" + (opts.direction === "horizontal" ? "horizontal" : "vertical") + (showLabels ? "" : " oc-no-labels");
  if (fab) fab.style.display = hasIcons ? "none" : "flex";
  iconsContainer.style.display = hasIcons ? "flex" : "none";
  iconsContainer.innerHTML = html;
}

export function mountContainer(theme: WidgetTheme, callbacks: ContainerCallbacks, buttonLogo?: string, aiAvatar?: string, displayMode: DisplayMode = "collapsed"): void {
  if (_shadowRoot) return;
  _theme = theme; _locale = theme.locale; _callbacks = callbacks; _displayMode = displayMode;
  const host = document.createElement("div"); host.id = "onecall-widget-host";
  const shadow = host.attachShadow({ mode: "open" }); _shadowRoot = shadow;
  const pos = theme.position || "bottom-right";
  shadow.innerHTML = '<style>' + generateStyles(theme) + '</style>' +
    '<div class="oc-container ' + pos + '">' +
      '<div class="oc-panel" id="oc-panel" role="dialog" aria-modal="true" aria-label="' + (theme.title || t("container.title", _locale)) + '">' +
        '<div class="oc-panel-header"><button class="oc-panel-close" id="oc-close" aria-label="' + t("container.close", _locale) + '">&times;</button><div class="oc-panel-header-top"><button class="oc-panel-back" id="oc-back" aria-label="' + t("container.back", _locale) + '">' + svgBack() + '</button><div class="oc-panel-title-row"><span class="oc-panel-title-icon">' + (aiAvatar ? '<img src="' + aiAvatar + '" alt="AI" style="width:100%;height:100%;border-radius:8px" />' : svgBot()) + '</span><span class="oc-panel-title">' + (theme.title || t("container.title", _locale)) + '</span></div></div>' +
          '<p class="oc-panel-subtitle"><span class="oc-subtitle-dot"></span>' + (theme.welcomeText || "") + '</p>' +
        '</div>' +
        '<div class="oc-panel-body" id="oc-body"></div>' +
        '<div class="oc-panel-footer">' + t("poweredBy", _locale) + '</div>' +
      '</div>' +
      '<div id="oc-icons" style="display:none"></div>' +
      '<button class="oc-fab pulse" id="oc-fab" aria-label="' + t("container.fabOpen", _locale) + '" aria-haspopup="dialog"><span class="oc-fab-icon oc-fab-icon-chat" style="width:100%;height:100%">' + (buttonLogo ? '<img src="' + buttonLogo + '" alt="chat" style="width:100%;height:100%;border-radius:50%" />' : svgChat()) + '</span><span class="oc-fab-icon oc-fab-icon-close">' + svgX() + '</span><span class="oc-fab-badge" id="oc-fab-badge" aria-live="polite"></span></button>' +
    '</div>';
  document.body.appendChild(host);
  const fab = $("#oc-fab"), close = $("#oc-close"), back = $("#oc-back");
  fab?.addEventListener("click", () => { if (_panelOpen) closePanel(); else openPanel(); });
  close?.addEventListener("click", (e) => { e.stopPropagation(); closePanel(); });
  back?.addEventListener("click", (e) => { e.stopPropagation(); _callbacks?.onPanelOpen(); });
  shadow.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest("[data-feature]") as HTMLElement;
    if (!btn) return;
    const feature = btn.getAttribute("data-feature") as Feature;
    const ch = btn.getAttribute("data-ch") || undefined;
    // 展开模式下的 IM 图标：直接深链跳转，不打开面板
    if (feature === "im" && ch) {
      const value = decodeURIComponent(btn.getAttribute("data-value") || "");
      const name = btn.getAttribute("data-name") || "";
      _callbacks?.onImClick?.(ch, value, name);
      return;
    }
    _activeFeature = feature; _panelOpen = false;
    if (_callbacks?.onPanelOpen) _callbacks.onPanelOpen(feature, ch);
    openPanelInternal();
  });
  // a11y：ESC 关闭面板（焦点在面板内时）
  shadow.addEventListener("keydown", (e: Event) => {
    if ((e as KeyboardEvent).key === "Escape" && _panelOpen) { e.stopPropagation(); closePanel(); }
  });
}

/** a11y：打开后把焦点移入面板（关闭按钮），关闭后焦点回到 FAB */
function focusPanel(): void {
  setTimeout(() => { ($("#oc-close") as HTMLElement | null)?.focus(); }, 60);
}

/** 未读消息角标（右下角 FAB 上） */
function renderBadge(): void {
  const b = $("#oc-fab-badge");
  if (!b) return;
  if (_unread > 0) { b.textContent = _unread > 99 ? "99+" : String(_unread); b.style.display = "flex"; }
  else { b.textContent = ""; b.style.display = "none"; }
}
/** 收到新消息时累加未读（仅面板关闭时计数，打开时用户正在看不计） */
export function bumpUnread(n: number = 1): void {
  if (_panelOpen) return;
  _unread += n > 0 ? n : 1;
  renderBadge();
}
/** 清零未读（打开面板时调用） */
export function clearUnread(): void { _unread = 0; renderBadge(); }

function openPanelInternal(): void {
  _panelOpen = true;
  clearUnread();
  $("#oc-panel")?.classList.add("open");
  const fab = $("#oc-fab");
  fab?.classList.remove("pulse");
  fab?.classList.add("oc-fab-close");
  // 展开模式：面板会盖住图标，打开时先隐藏图标栈
  if (_displayMode === "expanded") { const icons = $("#oc-icons"); if (icons) icons.style.display = "none"; }
  focusPanel();
}
function openPanel(): void {
  _panelOpen = true;
  clearUnread();
  $("#oc-panel")?.classList.add("open");
  const fab = $("#oc-fab");
  fab?.classList.remove("pulse");
  fab?.classList.add("oc-fab-close");
  _callbacks?.onPanelOpen();
  focusPanel();
}
function closePanel(): void {
  _panelOpen = false;
  $("#oc-panel")?.classList.remove("open");
  const fab = $("#oc-fab");
  _callbacks?.onPanelClose();
  if (_displayMode === "expanded") {
    // 展开模式：关闭面板后恢复图标栈，FAB 保持隐藏
    const icons = $("#oc-icons");
    if (icons) { icons.style.display = "flex"; (icons.querySelector(".oc-launch-btn") as HTMLElement | null)?.focus(); }
    if (fab) fab.style.display = "none";
    return;
  }
  fab?.classList.add("pulse");
  fab?.classList.remove("oc-fab-close");
  ($("#oc-fab") as HTMLElement | null)?.focus();
}

export function setActiveFeature(feature: Feature | null): void {
  _activeFeature = feature;
  const iconEl = $("#oc-badge-icon"), textEl = $("#oc-badge-text");
  if (iconEl && textEl) {
    if (feature) { iconEl.textContent = FEATURE_ICONS[feature]; textEl.textContent = FEATURE_LABELS[feature]; }
    else { iconEl.textContent = "⚠️"; textEl.textContent = t("container.noFeature", _locale); }
  }
}

export function setPanelContent(html: string): void { const body = $("#oc-body"); if (body) body.innerHTML = html; }
export function setFabPulse(active: boolean): void { const fab = $("#oc-fab"); if (active) fab?.classList.add("pulse"); else fab?.classList.remove("pulse"); }
export function getShadowRoot(): ShadowRoot | null { return _shadowRoot; }

/** 重置容器状态（用于 destroy 后重新 init） */
export { closePanel };

export function resetContainer(): void {
  _shadowRoot = null; _theme = null; _activeFeature = null; _panelOpen = false; _displayMode = "collapsed"; _unread = 0;
}

export function showPanelBack(show: boolean): void {
  const btn = $("#oc-back");
  if (btn) btn.style.display = show ? "flex" : "none";
}
export function getTheme(): WidgetTheme | null { return _theme; }
export function getLocale(): Locale { return _locale; }
export function getActiveFeature(): Feature | null { return _activeFeature; }
export function getFeatures(): Feature[] { return _features; }
